import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import {
  VIEWS_SUB_SCREENS,
  setCheckedEmployees,
  setViewsSubScreen,
  useCSVUploadEmployeeList,
  useCheckedBaseCohort,
  useCheckedEmployees,
} from 'reducers/viewsSlice';

import { ArrowCircleUp } from 'assets/assetsV1/iconComponents';

import { VIEWS_FILTER_KEYS } from 'constants/ProjectConstants';

import { Avatar } from 'uiToolkitV1/Avatar';
import { Button } from 'uiToolkitV1/Button';
import { Divider } from 'uiToolkitV1/Divider';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { Tooltip } from 'uiToolkitV1/Tooltip';

import { useEmployees } from '../../Hooks/dataHooks';
import { AgGridContainer } from './AgGridContainer';

const ProfileImage = ({ src, name }) => <Avatar src={src} name={name} />;

const CellRenderer = ({ data: employee }) => {
  const comp = useCallback(
    () => <ProfileImage src={employee.profilePhoto} name={employee.displayName} />,
    [employee.profilePhoto, employee.displayName]
  );

  return (
    <ListItemComp
      title={employee.displayName}
      subTitle={employee.designation}
      isBoldText={false}
      LeftComponent={comp}
      withoutBackground
      padding="16px 24px"
    />
  );
};

const UploadCsvButton = () => {
  const dispatch = useDispatch();

  return (
    <Tooltip label="Upload list of employees and select them">
      <Button
        variant="outlined"
        StartIcon={ArrowCircleUp}
        onClick={() => dispatch(setViewsSubScreen(VIEWS_SUB_SCREENS.UPLOAD_CSV))}
      >
        Upload CSV
      </Button>
    </Tooltip>
  );
};

export const Employees = () => {
  const checkedBaseCohort = useCheckedBaseCohort();
  const { data: employees, isLoading, isSuccess } = useEmployees({ cohortId: checkedBaseCohort?.uuid });
  const dispatch = useDispatch();
  const checkedEmployees = useCheckedEmployees();
  const csvEmployees = useCSVUploadEmployeeList();
  const [nonCohortApplicableEmployees, setNonCohortApplicableEmployees] = useState([]);

  const checkedRowsSet = useMemo(() => new Set(checkedEmployees ?? []), [checkedEmployees]);

  const columnDefs = useMemo(
    () => [
      {
        colId: VIEWS_FILTER_KEYS.EMPLOYEES,
        flex: 1,
        cellRenderer: CellRenderer,
        getQuickFilterText: (params) => params.data.displayName,
        sortable: true,
        comparator: (_valueA, _valueB, nodeA, nodeB, isDescending) => {
          // these checks are for moving checked rows to the top
          if (checkedRowsSet.has(nodeA.data.id) && checkedRowsSet.has(nodeB.data.id)) {
            const a = nodeA.data.displayName;
            const b = nodeB.data.displayName;
            return a.localeCompare(b);
          }
          if (checkedRowsSet.has(nodeA.data.id)) {
            return isDescending ? 1 : -1;
          }
          if (checkedRowsSet.has(nodeB.data.id)) {
            return isDescending ? -1 : 1;
          }
          const a = nodeA.data.displayName;
          const b = nodeB.data.displayName;
          return a.localeCompare(b);
        },
      },
    ],
    [checkedRowsSet]
  );

  useEffect(() => {
    if (!isSuccess) return;
    // let x = employees;
    if (csvEmployees && csvEmployees.length > 0) {
      const employeesIds = employees.map((e) => e.id);
      const csvEmployeesInCohort = csvEmployees.filter((e) => employeesIds.includes(e.id));
      const csvEmployeesNotInCohort = csvEmployees.filter((e) => !employeesIds.includes(e.id));
      setNonCohortApplicableEmployees(csvEmployeesNotInCohort);

      const nonDuplicateCheckedEmployees = [
        ...new Set([...(checkedEmployees ?? []), ...csvEmployeesInCohort.map((e) => e.id)]),
      ];
      dispatch(setCheckedEmployees(nonDuplicateCheckedEmployees));
    }
  }, [csvEmployees, dispatch, employees, isSuccess]);

  const memoizedRowCheckedTesterFn = useCallback(({ nodeData, checkedRows }) => {
    const { id: nodeDataId } = nodeData || {};
    return checkedRows?.find((e) => Number(e) === Number(nodeDataId));
  }, []);

  return (
    <>
      {nonCohortApplicableEmployees.length > 0 && (
        <Box>
          <Divider />
          <Box padding="10px">
            <Box fontWeight="bold" fontSize="14px" color="text.g3">
              CSV employees not in cohort:{' '}
              {nonCohortApplicableEmployees.map((e) => `${e.displayName}(${e.email})`).join(', ')}
            </Box>
          </Box>
        </Box>
      )}
      <AgGridContainer
        rowData={employees}
        columnDefs={columnDefs}
        setCheckedRows={setCheckedEmployees}
        sortColumnId={VIEWS_FILTER_KEYS.EMPLOYEES}
        checkedRows={checkedEmployees}
        ToolBarRightComponent={UploadCsvButton}
        rowHeight={60}
        rowCheckedTesterFn={memoizedRowCheckedTesterFn}
      />
    </>
  );
};
